import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ChevronDown, ChevronUp, Mail, ArrowLeft } from "lucide-react";
import logo from "./assets/imgiconlogo.png";

const EFFECTIVE_DATE = "13 June 2026";
const COMPANY = "TinyGlobal Pty Ltd";
const ABN = "42 327 513 086";
const CONTACT_EMAIL = "contact@yadata.com.au";
const WEBSITE = "tinyglobalop.com";

const sections = [
  {
    id: "overview",
    title: "1. Overview",
    content: `${COMPANY} (ABN ${ABN}) ("TinyGlobal", "we", "us", "our") operates a cloud-based logistics operating system accessible at ${WEBSITE} and related subdomains (the "Platform").

We are committed to protecting your personal information in accordance with the Privacy Act 1988 (Cth) ("Privacy Act"), the Australian Privacy Principles ("APPs") contained in Schedule 1 of the Privacy Act, and applicable New South Wales legislation including the Privacy and Personal Information Protection Act 1998 (NSW) ("PPIPA") where relevant.

This Privacy Policy explains how we collect, use, disclose, store, and protect your personal information when you use our Platform, visit our website, or communicate with us. By accessing or using TinyGlobal, you consent to the practices described in this Policy.`,
  },
  {
    id: "collect",
    title: "2. Personal Information We Collect",
    content: `We collect personal information that is reasonably necessary for our business functions. This includes:

**Account & Identity Information**
• Full name, job title, and role within your organisation
• Business email address and phone number
• Company name, ABN/ACN, and registered address
• Account credentials (passwords are stored as irreversible hashes — never in plain text)

**Business & Operational Data**
• Supplier information, purchase orders, shipment records, customs documentation, and inventory data you enter into the Platform
• Electronic Data Interchange (EDI) messages and transaction records
• Customs entry data, HS codes, ATT accreditation documentation, and ABF-related records

**Usage & Technical Information**
• IP address, browser type, operating system, and device identifiers
• Pages visited, features used, session duration, and clickstream data
• API access logs and integration activity

**Communications**
• Enquiries submitted via contact or demo request forms
• Support tickets and chat messages
• Email correspondence with our team

We do not intentionally collect sensitive information (as defined in the Privacy Act) such as health information, racial or ethnic origin, political opinions, or religious beliefs. If such information is incidentally provided, we will handle it in accordance with APP 3.`,
  },
  {
    id: "how-collect",
    title: "3. How We Collect Personal Information",
    content: `We collect personal information:

• **Directly from you** — when you register for an account, complete a demo request form, submit a support ticket, or communicate with us
• **From your organisation** — when an administrator creates your account or assigns you a role within their tenant
• **Automatically** — through cookies, server logs, and analytics tools when you use the Platform
• **From third parties** — including identity verification services, payment processors (Square), or integration partners where you have authorised such sharing
• **Via integrations** — when you connect third-party systems (ERPs, 3PLs, suppliers) to the Platform through our EDI or API connectors

We collect personal information by lawful and fair means, and where reasonable and practicable, only directly from you (APP 3.5 and 3.6).`,
  },
  {
    id: "use",
    title: "4. How We Use Your Personal Information",
    content: `We use personal information for the primary purpose for which it was collected, and for secondary purposes that you would reasonably expect, including:

**Provision of the Platform**
• Creating and managing your user account and organisational tenant
• Enabling logistics, supply chain, customs, and compliance workflows
• Facilitating EDI transactions, supplier collaboration, and warehouse operations
• Processing payments and managing subscriptions

**Communications**
• Responding to enquiries, support requests, and feedback
• Sending transactional emails (account creation, shipment updates, customs alerts, invoice reminders)
• Sending service announcements and operational notifications directly related to the Platform

**Security & Compliance**
• Authenticating users and detecting fraudulent or unauthorised activity
• Maintaining audit logs for regulatory compliance and post-clearance verification
• Complying with legal obligations, including requests from the Australian Border Force (ABF)

**Platform Improvement**
• Analysing aggregated usage data to improve features and performance
• Diagnosing technical issues and monitoring system health

We will not use your personal information for direct marketing without your consent, and we will always provide an easy opt-out mechanism in accordance with the Spam Act 2003 (Cth).`,
  },
  {
    id: "disclosure",
    title: "5. Disclosure of Personal Information",
    content: `We may disclose your personal information to:

**Service Providers & Subprocessors**
We engage trusted third-party providers to operate the Platform, including:
• Google Firebase / Firestore (cloud database and authentication — United States)
• Resend Inc. (transactional email delivery — United States)
• Square Inc. (payment processing — United States)
• Render (cloud hosting — United States)
• Vercel Inc. (frontend hosting — United States)
• Microsoft Azure (identity services — may involve international transfers)

All subprocessors are engaged under contractual arrangements that require them to protect personal information to a standard consistent with the APPs.

**Your Organisation**
Other authorised users within your company's TinyGlobal tenant may access information associated with your account as permitted by your administrator.

**Regulatory & Legal**
We may disclose personal information to regulatory bodies (including the ABF, ATO, or courts) where required by law, or to protect our legal rights.

**Business Transfers**
If TinyGlobal is acquired or merged with another entity, personal information may be transferred as part of that transaction, subject to the same privacy protections.

We do not sell personal information to third parties.`,
  },
  {
    id: "overseas",
    title: "6. Overseas Disclosure",
    content: `Some of our subprocessors operate outside Australia, primarily in the United States. By using the Platform, you consent to your personal information being transferred to, stored in, and processed in countries outside Australia, including the United States.

Before transferring personal information overseas, we take reasonable steps to ensure that overseas recipients do not breach the APPs in relation to that information, consistent with APP 8.

Where we transfer data to the United States, we rely on the contractual commitments of our subprocessors (including Google Cloud, Resend, and Square) who maintain privacy and security certifications appropriate to the handling of such data.`,
  },
  {
    id: "security",
    title: "7. Security",
    content: `We take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification, or disclosure, consistent with APP 11.

Our security measures include:
• Firebase Authentication with encrypted credential storage
• Role-based access controls (RBAC) enforced at API and Firestore levels
• HTTPS/TLS encryption for all data in transit
• All API keys and secrets stored as environment variables — never in source code or the database
• Passwords hashed using industry-standard algorithms — never stored in plain text
• Regular security reviews of API endpoints and access controls
• Audit logging for sensitive operations

Despite our efforts, no system is completely secure. If you suspect unauthorised access to your account, contact us immediately at ${CONTACT_EMAIL}.`,
  },
  {
    id: "ndb",
    title: "8. Notifiable Data Breaches",
    content: `We are subject to the Notifiable Data Breaches (NDB) scheme under Part IIIC of the Privacy Act. If we experience an eligible data breach — being a breach that is likely to result in serious harm to individuals — we will:

• Conduct a prompt assessment (within 30 days of becoming aware of the suspected breach)
• Notify the Office of the Australian Information Commissioner (OAIC) as soon as practicable
• Notify affected individuals directly where possible, or via a prominent notice on our website

We maintain an internal data breach response plan and will act in accordance with the OAIC's guidance.`,
  },
  {
    id: "access",
    title: "9. Access, Correction & Deletion",
    content: `Under APP 12 and APP 13, you have the right to:

• **Access** the personal information we hold about you
• **Correct** personal information that is inaccurate, out-of-date, incomplete, irrelevant, or misleading
• **Request deletion** of your personal information, subject to our legal retention obligations

To make a request, contact us at ${CONTACT_EMAIL} with the subject line "Privacy Request". We will respond within 30 days. We may ask you to verify your identity before actioning a request.

We may decline access where the Privacy Act permits us to do so (e.g. where it would unreasonably impact another person's privacy, or where the information relates to legal proceedings).

**Data Retention**
We retain personal information for as long as necessary to provide the Platform and comply with our legal obligations. Operational data (shipment records, customs entries, POs) may be retained for up to 7 years to support post-clearance audits and ATT maintenance requirements under Australian customs law.`,
  },
  {
    id: "cookies",
    title: "10. Cookies & Analytics",
    content: `Our website and Platform use cookies and similar technologies to:

• Maintain your authenticated session
• Remember your preferences and settings
• Analyse Platform usage through aggregated analytics (e.g. page views, feature adoption)
• Support security functions (CSRF protection, rate limiting)

We do not use third-party advertising cookies or cross-site tracking technologies. You can configure your browser to refuse cookies, but this may prevent certain features of the Platform from functioning correctly.`,
  },
  {
    id: "children",
    title: "11. Children's Privacy",
    content: `The Platform is designed for use by businesses and their authorised employees. We do not knowingly collect personal information from individuals under 18 years of age. If you believe a minor has provided us with personal information, please contact us at ${CONTACT_EMAIL} and we will take steps to remove it.`,
  },
  {
    id: "complaints",
    title: "12. Complaints",
    content: `If you have a complaint about how we handle your personal information, please contact us first:

**TinyGlobal Privacy Contact**
Email: ${CONTACT_EMAIL}
Subject: "Privacy Complaint"

We will acknowledge your complaint within 5 business days and aim to resolve it within 30 days.

If you are not satisfied with our response, you may lodge a complaint with the **Office of the Australian Information Commissioner (OAIC)**:
• Website: www.oaic.gov.au
• Phone: 1300 363 992
• Post: GPO Box 5218, Sydney NSW 2001

NSW residents may also contact the **NSW Privacy Commissioner** (for complaints relating to NSW public sector agencies) or the **NSW Civil and Administrative Tribunal (NCAT)** where applicable.`,
  },
  {
    id: "changes",
    title: "13. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will notify you of material changes by:

• Posting the updated Policy on our website with a revised effective date
• Sending an in-platform notification or email where the change significantly affects your rights

Continued use of the Platform after the updated Policy takes effect constitutes your acceptance of the revised terms.`,
  },
  {
    id: "contact",
    title: "14. Contact Us",
    content: `For any privacy-related enquiries, requests, or complaints:

**TinyGlobal Pty Ltd**
ABN: ${ABN}
Sydney, New South Wales, Australia

**Built & operated by Yadata Pty Ltd**
Email: ${CONTACT_EMAIL}
Website: www.yadata.com.au`,
  },
];

function Section({ s }) {
  const [open, setOpen] = useState(true);

  const renderContent = (text) => {
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} className="h-2" />;
      // Bold **text**
      const parts = line.split(/\*\*(.*?)\*\*/g);
      const rendered = parts.map((p, j) => j % 2 === 1 ? <strong key={j} className="text-navy-900 font-semibold">{p}</strong> : p);
      // Bullet points
      if (line.trim().startsWith("•")) {
        return (
          <div key={i} className="flex items-start gap-2 ml-2 mb-1">
            <span className="text-gold-500 mt-1.5 shrink-0">•</span>
            <p className="text-slate-600 text-sm leading-relaxed">{rendered}</p>
          </div>
        );
      }
      return <p key={i} className="text-slate-600 text-sm leading-relaxed mb-1">{rendered}</p>;
    });
  };

  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden mb-4 shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <h2 className="font-bold text-navy-900 text-base">{s.title}</h2>
        {open ? <ChevronUp size={16} className="text-slate-400 shrink-0" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-6 pt-2 bg-white border-t border-slate-100 space-y-1">
          {renderContent(s.content)}
        </div>
      )}
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200">
              <img src={logo} alt="TinyGlobal" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-navy-900">TinyGlobal</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-900 transition-colors font-medium">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gold-500/20 border border-gold-500/30 flex items-center justify-center">
              <Shield size={20} className="text-gold-400" />
            </div>
            <span className="text-gold-400 text-xs font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-3">Privacy Policy</h1>
          <p className="text-slate-400 text-sm">
            Effective Date: {EFFECTIVE_DATE} &nbsp;·&nbsp; {COMPANY} (ABN {ABN})
          </p>
          <p className="text-slate-300 text-sm mt-3 max-w-2xl leading-relaxed">
            This policy governs how TinyGlobal collects, uses, and protects your personal information in accordance with the <em>Privacy Act 1988</em> (Cth), the Australian Privacy Principles (APPs), and applicable New South Wales legislation.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {sections.map(s => <Section key={s.id} s={s} />)}

        {/* Contact card */}
        <div className="mt-8 p-6 bg-navy-900 rounded-2xl text-white">
          <div className="flex items-start gap-4">
            <Mail size={20} className="text-gold-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold mb-1">Privacy enquiries</p>
              <p className="text-slate-300 text-sm">Contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold-400 hover:text-gold-300 transition-colors">{CONTACT_EMAIL}</a> with the subject "Privacy Request" or "Privacy Complaint".</p>
              <p className="text-slate-400 text-xs mt-2">{COMPANY} · ABN {ABN} · Sydney NSW · Built by Yadata</p>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-8 pb-8">
          © {new Date().getFullYear()} {COMPANY} ABN {ABN} · <Link to="/terms" className="hover:text-navy-900 transition-colors">Terms of Service</Link> · <Link to="/" className="hover:text-navy-900 transition-colors">Home</Link>
        </p>
      </div>
    </div>
  );
}
